import { opensNewTab, readArticleLinkKey } from './article-links.js';

/**
 * The fallback of a help link (data-leadwin-article): the link opens its article in the panel instead of
 * navigating, and goes where it points after all once the widget cannot show it.
 *
 * The contract with the frame (its half is wwwroot/js/widget-host-article.js):
 *  - Every request carries an id, and the frame answers it exactly once under that id: articleshown,
 *    articleunavailable or articledismissed (the visitor left the article first). An answer under any
 *    other id is ignored, so a late one for an older click cannot move this one.
 *  - The patience measures whether the frame is alive, not how long an article takes: it runs from the
 *    click and starts afresh when the circuit subscribes, so a cold start is not cut short, while a
 *    circuit that paused and never comes back still ends in the link's own page.
 *  - Following the link takes the request back (feedbax:widget:forgetarticle): its article must not open
 *    later over the page the visitor was sent to.
 */

/* How long a help link waits for the frame to show signs of life: from the click, and again from the
 * circuit's subscription. */
export const articleLinkPatienceMs = 8000;

/* The frame's answers, by message type. */
const answers = {
    'feedbax:widget:articleshown': 'shown',
    'feedbax:widget:articleunavailable': 'unavailable',
    'feedbax:widget:articledismissed': 'dismissed'
};

/**
 * Whether a message from the frame answers an article request.
 * @param {unknown} type - The message's type
 * @returns {boolean}
 */
export function isArticleAnswer(type) {
    return typeof type === 'string' && Object.prototype.hasOwnProperty.call(answers, type);
}

/**
 * @param {object} ctx - What the fallback needs from the instance, late-bound where the controls are
 * @param {object} ctx.state - The instance's shared state (panelOpen)
 * @param {HTMLElement} ctx.button - The launcher; its absence ends the instance
 * @param {(key: string, requestId: number) => boolean} ctx.openArticle - Opens the panel on an article
 * @param {(open: boolean) => void} ctx.setOpen - Opens or closes the panel
 * @param {() => void} ctx.forgetPendingArticle - Drops an article still waiting for the circuit
 * @param {(requestId: number) => void} ctx.forgetArticleRequest - Takes a request back from the frame
 */
export function createArticleLinkFallback(ctx) {
    const { state, button } = ctx;

    /* The help link whose request the frame has not answered yet, and the one link whose next click is
     * the fallback and must pass untouched. */
    let pending = null;
    let following = null;
    let lastRequestId = 0;

    /**
     * One data-leadwin-article click. The link's own navigation is cancelled only once the panel has
     * taken the article: an address that names no article, a loader that outlived its page, or a click
     * with a modifier (Ctrl, Cmd, Shift: the visitor's way of saying "somewhere else") all leave the link
     * to do what a link does, which is why the attribute belongs on one. So does a link that opens a new
     * tab (see opensNewTab): its fallback could not keep that promise.
     * @param {HTMLElement} link - Element carrying data-leadwin-article
     * @param {MouseEvent} event - The click
     */
    function open(link, event) {
        if (link === following) {
            return; // The widget could not show it, and this is the link going where it points after all.
        }

        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
            return;
        }

        try {
            if (opensNewTab(link)) {
                return;
            }

            const key = readArticleLinkKey(link);
            const wasOpen = state.panelOpen;
            const requestId = ++lastRequestId;

            if (!button.isConnected || !ctx.openArticle(key, requestId)) {
                return;
            }

            event.preventDefault();

            // Only a link has a page to fall back to; a button carrying the attribute is the JS API
            // without script, and like the JS API it simply opens the article.
            if (typeof link.href === 'string' && link.href) {
                pending = { requestId: requestId, link: link, href: link.href, wasOpen: wasOpen, timer: null };
                renewPatience();
            }
        } catch { /* the API must never throw into the host page; the link navigates instead */ }
    }

    /** Starts the waiting link's patience afresh: at the click, and when the circuit subscribes. */
    function renewPatience() {
        if (!pending) {
            return;
        }

        const waiting = pending;
        clearTimeout(waiting.timer);
        waiting.timer = setTimeout(function () {
            follow(waiting);
        }, articleLinkPatienceMs);
    }

    /**
     * Lets go of the help link still waiting, without following it: the visitor closed the panel or asked
     * the widget for something else. Handed to the panel controls, which see every close and every
     * other open.
     */
    function forget() {
        if (pending) {
            clearTimeout(pending.timer);
            pending = null;
        }
    }

    /**
     * The frame's answer to a request. Shown or dismissed, the link has nothing left to fall back for;
     * unavailable (unpublished, help center off, another agent's, a circuit that refused the request), it
     * does what it would have done without the widget.
     * @param {{type: string, requestId: unknown}} answer - The frame's message
     */
    function settle(answer) {
        if (!pending || answer.requestId !== pending.requestId) {
            return;
        }

        if (answers[answer.type] === 'unavailable') {
            follow(pending);
        } else {
            forget();
        }
    }

    /** The frame is reloading: the circuit that took the request is gone, and its answer with it. */
    function giveUp() {
        if (pending) {
            follow(pending);
        }
    }

    /**
     * Sends the visitor where a waiting help link points: its own click runs once more, past the handler
     * above, so the browser navigates the way the page wrote it. A link the page has since taken out of
     * the document is followed by its address instead.
     * @param {{requestId: number, link: HTMLElement, href: string, wasOpen: boolean}} waiting - The link
     */
    function follow(waiting) {
        if (waiting !== pending || !button.isConnected) {
            return; // Let go of in the meantime, or an instance whose page is gone.
        }

        // Always, whatever the panel does next: an article still parked for the circuit, or already on its
        // way in it, would open later over the page the visitor is being sent to, wherever that page does
        // not reload.
        ctx.forgetPendingArticle();
        ctx.forgetArticleRequest(waiting.requestId);
        forget();

        if (!waiting.wasOpen) {
            ctx.setOpen(false);
        }

        if (waiting.link.isConnected) {
            following = waiting.link;

            try {
                waiting.link.click();
            } finally {
                // Synchronous: the click has passed the handler above by now, or a host handler stopped it
                // on the way. Either way the next real click on this link is the widget's again.
                following = null;
            }
        } else {
            globalThis.location.assign(waiting.href);
        }
    }

    return { open, forget, settle, giveUp, renewPatience };
}
